import { MEAL_PLAN_REGIONS_IDS, ZIP_CODE_REGEX } from 'common/Constants';
import { formatGqlErrors } from 'common/GqlUtilities';
import { supportsMealPlans } from 'common/MealPlanUtils';
import { Slugs } from 'common/urls/Slugs';
import qs from 'query-string';
import { useCallback, useMemo, useState } from 'react';
import { ZipCodeAvailabilityDocument, ZipCodeAvailabilityQuery } from 'src/gqlReactTypings.generated.d';
import { isInsideIntake } from 'src/pages/consumer/meal-plans/consts';
import { useMealPlanSurfacingLogic } from 'src/pages/consumer/meal-plans/hooks/useMealPlanSurfacingLogic';
import { useHistory } from 'src/shared/hooks/useHistory';
import { useLocation } from 'src/shared/hooks/useLocation';
import { Routes } from 'src/shared/Routes';
import { useGqlClient } from 'src/shared/useGqlClient';
import { EXTRA_SLUGS, getSluggedPath, QUERY_PARAMS } from 'src/shared/utils/RouteUtilities';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';
import { useCurrentUser } from '../shared/hooks/useCurrentUserHook';

interface UseZipCodeInputParams {
  onZipSubmit?: (zipCode: string) => void;
  isMealPlan?: boolean;
  redirectRoute?: string;
  extraSlugs?: EXTRA_SLUGS;
  queryParams?: QUERY_PARAMS;
  redirectOnSubmit?: boolean;
}

export const useZipCodeInput = ({
  extraSlugs = [],
  queryParams = {},
  redirectOnSubmit = true,
  redirectRoute = Routes.CONSUMER_EXPLORE,
  isMealPlan = false,
  onZipSubmit,
}: UseZipCodeInputParams) => {
  const history = useHistory();
  const location = useLocation();
  const [cachedZipCode, setCachedZipCode] = useCachedZipCode();
  const { getMealPlanUrl } = useMealPlanSurfacingLogic();
  const GqlClient = useGqlClient();
  const [currentUser] = useCurrentUser();
  const firstStepRoute = getMealPlanUrl({});
  const [zipCode, setZipCode] = useState(cachedZipCode?.trim() ?? '');

  const isZipValid = useMemo(() => ZIP_CODE_REGEX.test(zipCode), [zipCode]);

  const onChange = useCallback((newZipCode: string) => {
    setZipCode(newZipCode.trim());
  }, []);

  /**
   * Redirects to meal plan intake first step with query parameters.
   * Only called when redirectOnSubmit=true and isMealPlan=true.
   *
   * NOTE: This is NOT called from homepage meal plan flow, which uses
   * redirectOnSubmit=false and handles redirects via useHomepageRedirects.
   * Long-term: we want to consolidate to a single-layer submit-and-redirect
   * flow owned by this hook to avoid races and complexity.
   *
   * @param _ - Zip code parameter (unused, route determined by region logic)
   */
  const mealPlanRedirect = useCallback(() => {
    // Already in the intake: the zip is cached and its queries refetch on their own, so there is
    // nowhere to send the user and redirecting would restart the flow.
    if (isInsideIntake(location)) {
      return;
    }
    // firstStepRoute carries this arm's entry step; a hardcoded id sends the other arm to a step
    // its order does not contain.
    const { url, query } = qs.parseUrl(firstStepRoute);
    const path = qs.stringifyUrl({
      url,
      // Include zip in query so preferences can hydrate immediately even if
      // cached zip propagation lags.
      query: { ...query, zip: zipCode, ...queryParams },
    });
    history.push(path);
  }, [firstStepRoute, history, location, queryParams, zipCode]);

  /**
   * Handles zip code submission with two possible flows:
   *
   * FLOW 1 - redirectOnSubmit=true (default):
   * - Validates zip, caches it, queries ZipCodeAvailability
   * - Handles redirect logic directly (meal plan intake, waitlist, explore)
   * - Used by: ZipCodeInput, ModalZipCodeInput, HeaderZipCodeInput
   *
   * FLOW 2 - redirectOnSubmit=false (homepage meal plan flow):
   * - Validates zip, caches it, then returns early
   * - No redirect handled here - delegated to calling component
   * - Used by: Homepage meal plan button → useHomepageRedirects reacts to cached zip
   *
   * This two-layer architecture allows components to either use built-in redirect
   * logic or implement custom redirect behavior based on global state changes.
   */
  const onSubmit = useCallback(async () => {
    if (!isZipValid) return;
    onZipSubmit?.(zipCode);

    const { data, errors } = await GqlClient.query<ZipCodeAvailabilityQuery>({
      query: ZipCodeAvailabilityDocument,
      variables: { zipCode },
      fetchPolicy: 'network-only',
    });

    if (errors) {
      console.error(formatGqlErrors(errors));
    }

    const route = getSluggedPath(redirectRoute, [{ slug: Slugs.ZIP_CODE, value: zipCode }, ...extraSlugs], queryParams);

    setZipCode('');

    const regionId = data.zipCode?.region?.id ?? 0;
    const deliveryHubId = data.zipCode?.deliveryHubId ?? null;
    const isInMealPlanRegion = supportsMealPlans({
      regionId,
      deliveryHubId,
    });
    if (redirectOnSubmit && isMealPlan && isInMealPlanRegion) {
      // Ensure zip is cached before route change to avoid race conditions
      // where the destination page mounts without a cached zip.
      setCachedZipCode(zipCode);
      mealPlanRedirect();
      return;
    }

    setCachedZipCode(zipCode);

    // Early return for custom redirect handling (e.g., homepage meal plan flow).
    // Long-term: prefer single-layer submit-and-redirect and remove this mode.
    if (!redirectOnSubmit) return;

    const mealPlanRegionsIds = MEAL_PLAN_REGIONS_IDS;
    if (redirectRoute === firstStepRoute && !mealPlanRegionsIds.includes(data.zipCode?.region?.id ?? 0)) {
      /**
       * Hack for meal-plans test
       * If the users region isn't one of the 6 major regions (including New Jersey)
       * Redirect them to the waitlist page
       */
      history.push(Routes.WAITLIST_HOME);
    } else if (data.zipCode?.region?.isInWaitlist && !currentUser) {
      history.push(getSluggedPath(Routes.CONSUMER_REGISTER, [{ slug: Slugs.REDIRECT, value: route }]));
    } else {
      history.push(route);
    }
  }, [
    isZipValid,
    onZipSubmit,
    zipCode,
    GqlClient,
    redirectRoute,
    extraSlugs,
    queryParams,
    currentUser,
    history,
    // Added for correctness to avoid stale closures
    firstStepRoute,
    isMealPlan,
    mealPlanRedirect,
    redirectOnSubmit,
    setCachedZipCode,
  ]);

  return {
    cachedZipCode,
    zipCode,
    onSubmit,
    onChange,
  };
};
